import type { ParamValue, GraphEdge, GraphNode } from '../graph/nodeDefs.js';
import type { ArcCoverageMode } from '../sdf/arcCoverageMode.js';
import {
  type Scene,
  type ComponentDef,
  createDefaultScene,
} from '../scene/sceneState.js';
import {
  type GraphLibrary,
  type LayerDef,
  type LibraryGeometryDef,
  type LibraryMaterialDef,
} from '../scene/layerState.js';
import { createDocumentEntityId } from '../ids/entityIds.js';
import type { DataGraphDef, DataGraphInstance, DataSourceDef, QueryGraphDef } from '../graph/dataGraph.js';
import type { EventGraphDef, RuntimeStateSchema } from '../graph/eventGraph.js';
import type { SvgPathShape } from '../graph/shapeAssets.js';
import type { DocumentLight } from '../graph/lights.js';

export type { SvgPathShape } from '../graph/shapeAssets.js';

/**
 * The record and enum types the document declares (`types`), as the engine
 * writes them: carried for the engine, which reads them; the web edits none.
 */
export type DocumentTypeDeclarations = Record<string, unknown>;

export type EditorGraphSlot = 'geometry' | 'material';
export type EditorGraphKind = EditorGraphSlot | 'data' | 'event' | 'query';
export type RuntimeGraphKind = Extract<EditorGraphKind, 'data' | 'event' | 'query'>;
export type RenderSlot = EditorGraphSlot;
export type EditorGraphMode = 'auto' | RenderSlot;
export type Colormap = 'gray' | 'inferno' | 'plasma' | 'turbo';
export type AaMode = 'none' | 'fwidth' | 'msaa2x';
export type RenderBackend = 'canvas' | 'webgpu';
export type ViewportSizeMode = 'fixed' | 'fit';
export type ViewportMode = 'scene' | 'focus' | 'isolation';

export type SceneFavoriteTarget =
  | { type: 'scene' }
  | { type: 'component'; componentId: string }
  | { type: 'node'; componentId?: string; nodeId: string };

/**
 * The entity whose details occupy the right Inspector. Scene node selection
 * remains owned by SceneTreeSelectionController; this target only selects the
 * inspector domain.
 */
export type EmptyInspectorPanel = 'actions' | 'favorites' | 'geometry' | 'materials' | 'history' | 'hotkeys' | 'settings';

export type GraphLibraryInspectorTarget = { type: 'graph-library'; kind: RuntimeGraphKind };
export type GraphInspectorTarget = { type: 'graph'; kind: RuntimeGraphKind; graphId: string };
export type NewGraphInspectorTarget = {
  type: 'new-graph';
  kind: RuntimeGraphKind;
  returnTarget: GraphLibraryInspectorTarget | GraphInspectorTarget;
};

export type InspectorTarget =
  | { type: 'scene' }
  | { type: 'panel'; panel: EmptyInspectorPanel }
  | GraphLibraryInspectorTarget
  | GraphInspectorTarget
  | NewGraphInspectorTarget
  | { type: 'shape-library' }
  | { type: 'shape'; shapeId: string; builtIn: boolean }
  | { type: 'new-shape'; returnTarget: { type: 'shape-library' } | { type: 'shape'; shapeId: string; builtIn: boolean } }
  | { type: 'runtime' }
  | { type: 'data-source'; sourceId: string }
  | { type: 'runtime-state'; stateName: string };

export interface SceneFavorite {
  id: string;
  target: SceneFavoriteTarget;
}

export type ViewportTarget =
  | { type: 'scene' }
  | { type: 'component'; componentId: string }
  | { type: 'layer'; componentId?: string; layerId: string; sceneNodeId?: string }
  | { type: 'frame'; componentId?: string; frameId: string }
  | { type: 'geometry-library'; libraryItemId: string }
  | { type: 'material-library'; libraryItemId: string };

export type EditorContext =
  | { mode: 'layer'; componentId?: string; layerId?: string; layerSlot: 'geometry' | 'material'; libraryItemId?: string; frameId?: string }
  | { mode: 'geometry-library'; libraryItemId: string; componentId?: string; layerId?: string; layerSlot?: 'geometry' | 'material'; frameId?: string }
  | { mode: 'material-library'; libraryItemId: string; componentId?: string; layerId?: string; layerSlot?: 'geometry' | 'material'; frameId?: string }
  | { mode: 'data-library'; libraryItemId: string }
  | { mode: 'event-library'; libraryItemId: string }
  | { mode: 'query-library'; libraryItemId: string };

export interface AppState {
  documentId: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  editorGraphDirty: boolean;
  params: Record<string, ParamValue>;
  selectedNode: string | null;
  editorGraphSlot: EditorGraphSlot;
  editorGraphMode: EditorGraphMode;
  editorGraphKind: EditorGraphKind;
  activeDataGraphId: string | null;
  /** UI-only selection for the right Inspector; not part of the document. */
  inspectorTarget: InspectorTarget;
  activeEventGraphId: string | null;
  activeQueryGraphId: string | null;
  runtimePreview: boolean;
  colormap: Colormap;
  aaMode: AaMode;
  aaWidth: number;
  arcCoverageMode: ArcCoverageMode;
  resolution: number;
  viewportSizeMode: ViewportSizeMode;
  viewportMode: ViewportMode;
  viewportIsolationTarget: ViewportTarget | null;
  backend: RenderBackend;
  scene: Scene;
  componentLibrary: Record<string, ComponentDef>;
  layerLibrary: Record<string, LayerDef>;
  sceneFavorites: SceneFavorite[];
  activeComponentId: string | null;
  geometryLibrary: GraphLibrary<LibraryGeometryDef>;
  materialLibrary: GraphLibrary<LibraryMaterialDef>;
  shapes: Record<string, SvgPathShape>;
  /** Engine-owned read-only Shapes; excluded from canonical snapshots. */
  builtInShapes: Record<string, SvgPathShape>;
  dataGraphs: Record<string, DataGraphDef>;
  queryGraphs: Record<string, QueryGraphDef>;
  dataSources: Record<string, DataSourceDef>;
  data: DataGraphInstance | null;
  runtimeState: RuntimeStateSchema;
  eventGraphs: Record<string, EventGraphDef>;
  lights: DocumentLight[];
  types: DocumentTypeDeclarations;
  editorContext: EditorContext;
}

export function toRenderSlot(slot: EditorGraphSlot): RenderSlot {
  return slot;
}

export function createDefaultAppState(): AppState {
  return {
    documentId: createDocumentEntityId(),
    nodes: [],
    edges: [],
    editorGraphDirty: false,
    params: {},
    selectedNode: null,
    editorGraphSlot: 'geometry',
    editorGraphMode: 'auto',
    editorGraphKind: 'geometry',
    activeDataGraphId: null,
    inspectorTarget: { type: 'scene' },
    activeEventGraphId: null,
    activeQueryGraphId: null,
    runtimePreview: false,
    colormap: 'inferno',
    aaMode: 'fwidth',
    aaWidth: 0.5,
    arcCoverageMode: 'exact',
    resolution: 512,
    viewportSizeMode: 'fit',
    viewportMode: 'scene',
    viewportIsolationTarget: null,
    backend: 'webgpu',
    scene: createDefaultScene(),
    componentLibrary: {},
    layerLibrary: {},
    sceneFavorites: [],
    activeComponentId: null,
    geometryLibrary: {
      builtIn: {},
      user: {},
    },
    materialLibrary: {
      builtIn: {},
      user: {},
    },
    shapes: {},
    builtInShapes: {},
    dataGraphs: {},
    queryGraphs: {},
    dataSources: {},
    data: null,
    runtimeState: {},
    eventGraphs: {},
    lights: [],
    types: {},
    editorContext: {
      mode: 'layer',
      componentId: undefined,
      layerId: undefined,
      layerSlot: 'geometry',
    },
  };
}

export function resetAppState(appState: AppState, next: AppState = createDefaultAppState()): void {
  Object.assign(appState, next);
}

export const state: AppState = createDefaultAppState();
